import { ObjectMapper } from 'json-object-mapper';
import { parsePreRegistrationStatus } from './enum/PreRegistrationStatus.enum';
import { IModel } from '../IModel';
import { Model } from '../model';
import { RecruitmentAdmissionCustomFieldAnswerModel } from './recruitment-admission-custom-field-answer.model';

export class RecruitmentCandidateModel extends Model implements IModel<RecruitmentCandidateModel> {
  public fullName: string = '';
  public name: string = '';
  public email: string = '';
  public phone: string = '';
  public cellPhone: string = '';
  public status: string = '';
  public statusName: string = '';
  public pipelineStageName: string = '';
  public recruitmentTitle: string = '';
  public companyGroupId: string = '';
  public appliedDate: string = '';
  public experience: string = '';
  public skills: string[] = [];
  public education: string = '';
  public linkedin: string = '';
  public professionalSummary: string = '';
  public state: string = '';
  public city: string = '';
  public birthDate: string = '';
  public yearsOld: number = 0;
  public photoFileName: string = '';
  public photoFileKey: string = '';
  public photoFileSize: number = 0;
  public curriculumFileName: string = '';
  public curriculumFileKey: string = '';
  public curriculumFileSize: number = 0;
  public resumeFileKey: string = '';
  public resumeFileName: string = '';
  public currentPosition: string = '';
  public currentCompany: string = '';
  public expectedSalary: number = 0;
  public notes: string = '';
  public rating: number = 0;
  public source: string = '';
  public isInTalentBank: boolean = false;
  public qualifiedJobTitleGroupId: string | null = null;
  public hasPendingReassignmentInvitation: boolean = false;
  public interviewDate?: string;
  public rejectionReason?: string;
  public rejectionComments?: string;
  public rejectedDate?: string;
  public preRegistrationStatus?: number;
  public preRegistrationExpirationDate?: string;
  public qualificationExpirationDate?: string | null;
  public cpf: string = '';
  public addressLine1: string = '';
  public addressLine2: string = '';
  public district: string = '';
  public postalCode: string = '';
  public admissionCustomFieldAnswers: RecruitmentAdmissionCustomFieldAnswerModel[] = [];
  public customFieldsAnswer: any[] = [];
  public milestones: any[] = [];
  public schedules: any[] = [];
  public scheduleStatus: string = 'None';
  public scheduleDeclineReason: string | null = null;
  public positions: any[] = [];
  public institutionType?: string;
  public classification?: string;
  public additionalInfo?: any;
  public referredByEmployeeId?: string;
  public referralCode?: string;

  static create(input: any): RecruitmentCandidateModel {
    if (!input) {
      return new RecruitmentCandidateModel();
    }

    const candidate = ObjectMapper.deserialize(RecruitmentCandidateModel, input);
    const normalizeString = (value?: any) => (value ? String(value).trim() : '');

    candidate.id = input.id || candidate.id;
    candidate.creationDate = input.creationDate || candidate.creationDate;
    candidate.changeDate = input.changeDate || candidate.changeDate;

    // Dados básicos
    candidate.fullName = normalizeString(input.fullName) || normalizeString(candidate.name);
    candidate.name =
      normalizeString(candidate.name) ||
      normalizeString(input.fullName) ||
      normalizeString(input.name);
    candidate.email = normalizeString(candidate.email) || normalizeString(input.email);
    candidate.phone =
      normalizeString(candidate.phone) ||
      normalizeString(input.cellPhone) ||
      normalizeString(input.phone);
    candidate.cellPhone = normalizeString(input.cellPhone) || candidate.phone;
    const preRegParsed = parsePreRegistrationStatus(
      input.preRegistrationStatus ?? input.PreRegistrationStatus
    );
    if (preRegParsed !== undefined) {
      candidate.preRegistrationStatus = preRegParsed;
    }
    const preRegExp =
      input.preRegistrationExpirationDate ?? input.PreRegistrationExpirationDate;
    if (preRegExp !== undefined && preRegExp !== null && preRegExp !== '') {
      candidate.preRegistrationExpirationDate = String(preRegExp);
    }
    candidate.cpf = normalizeString(input.cpf ?? input.Cpf);
    candidate.addressLine1 = normalizeString(input.addressLine1 ?? input.AddressLine1);
    candidate.addressLine2 = normalizeString(input.addressLine2 ?? input.AddressLine2);
    candidate.district = normalizeString(input.district ?? input.District);
    candidate.postalCode = normalizeString(input.postalCode ?? input.PostalCode);
    const qualificationExp =
      input.qualificationExpirationDate ?? input.QualificationExpirationDate;
    candidate.qualificationExpirationDate =
      qualificationExp !== undefined && qualificationExp !== null && qualificationExp !== ''
        ? String(qualificationExp)
        : null;
    candidate.linkedin = normalizeString(input.linkedin) || '';
    candidate.professionalSummary = normalizeString(input.professionalSummary) || '';

    // Localização
    candidate.state = normalizeString(input.state) || '';
    candidate.city = normalizeString(input.city) || '';
    candidate.birthDate = input.birthDate || '';
    candidate.yearsOld = input.yearsOld || 0;

    // IDs e relacionamentos
    candidate.recruitmentTitle = normalizeString(input.recruitmentTitle) || '';
    candidate.companyGroupId = input.companyGroupId || '';
    candidate.pipelineStageName =
      normalizeString(candidate.pipelineStageName) || normalizeString(input.pipelineStageName);

    // Status
    candidate.status =
      normalizeString(candidate.status) ||
      normalizeString(input.status) ||
      normalizeString(input.pipelineStageName);
    candidate.statusName =
      normalizeString(candidate.statusName) ||
      normalizeString(input.statusName) ||
      candidate.status;
    candidate.isInTalentBank = input.isInTalentBank || false;
    candidate.hasPendingReassignmentInvitation =
      input.hasPendingReassignmentInvitation === true ||
      input.hasPendingReassignmentInvitation === 'true';
    if (input.qualifiedJobTitleGroupId === null || input.qualifiedJobTitleGroupId === '') {
      candidate.qualifiedJobTitleGroupId = null;
    } else if (input.qualifiedJobTitleGroupId !== undefined) {
      candidate.qualifiedJobTitleGroupId = String(input.qualifiedJobTitleGroupId);
    }

    // Arquivos - Foto
    candidate.photoFileName = normalizeString(input.photoFileName) || '';
    candidate.photoFileKey = normalizeString(input.photoFileKey) || '';
    candidate.photoFileSize = input.photoFileSize || 0;

    // Arquivos - Currículo
    candidate.curriculumFileName = normalizeString(input.curriculumFileName) || '';
    candidate.curriculumFileKey = normalizeString(input.curriculumFileKey) || '';
    candidate.curriculumFileSize = input.curriculumFileSize || 0;
    candidate.resumeFileKey =
      normalizeString(candidate.resumeFileKey) ||
      normalizeString(input.resumeFileKey) ||
      normalizeString(input.curriculumFileKey);
    candidate.resumeFileName =
      normalizeString(candidate.resumeFileName) ||
      normalizeString(input.resumeFileName) ||
      normalizeString(input.curriculumFileName);

    // Data de aplicação
    if (!candidate.appliedDate && input.creationDate) candidate.appliedDate = input.creationDate;

    // Skills
    if (!candidate.skills?.length && input.skills?.length) candidate.skills = input.skills;

    // Salário
    if (
      candidate.expectedSalary === 0 &&
      input.expectedSalary !== undefined &&
      input.expectedSalary !== null
    )
      candidate.expectedSalary = input.expectedSalary;

    // Arrays complexos
    candidate.customFieldsAnswer =
      input.customFieldsAnswer || input.CustomFieldsAnswer || candidate.customFieldsAnswer || [];
    candidate.admissionCustomFieldAnswers = mapAdmissionCustomFieldAnswers(
      input.admissionCustomFieldAnswers ?? input.AdmissionCustomFieldAnswers
    );
    candidate.milestones = input.milestones || input.Milestones || candidate.milestones || [];
    candidate.schedules = input.schedules || [];
    candidate.scheduleStatus = normalizeString(input.scheduleStatus) || 'None';
    candidate.scheduleDeclineReason =
      input.scheduleDeclineReason == null || input.scheduleDeclineReason === ''
        ? null
        : String(input.scheduleDeclineReason);
    candidate.positions = input.positions || input.Positions || candidate.positions || [];

    return candidate;
  }

  serialize(): any {
    return {
      id: this.id,
      name: this.name,
      email: this.email,
      phone: this.phone,
      status: this.status,
      statusName: this.statusName,
      pipelineStageName: this.pipelineStageName,
      appliedDate: this.appliedDate,
      experience: this.experience,
      skills: this.skills,
      education: this.education,
      resumeFileKey: this.resumeFileKey,
      resumeFileName: this.resumeFileName,
      currentPosition: this.currentPosition,
      currentCompany: this.currentCompany,
      expectedSalary: this.expectedSalary,
      notes: this.notes,
      rating: this.rating,
      source: this.source,
      interviewDate: this.interviewDate,
      rejectionReason: this.rejectionReason,
      rejectionComments: this.rejectionComments,
      rejectedDate: this.rejectedDate,
      creationDate: this.creationDate,
      changeDate: this.changeDate,
      institutionType: this.institutionType,
      classification: this.classification,
      additionalInfo: this.additionalInfo,
    };
  }

  serializeForApplication(): any {
    return {
      fullName: this.fullName || this.name,
      email: this.email,
      cellPhone: this.cellPhone || this.phone,
      linkedin: this.linkedin,
      professionalSummary: this.professionalSummary,
      state: this.state,
      city: this.city,
      birthDate: this.birthDate,
      education: this.education,
      currentPosition: this.currentPosition,
      currentCompany: this.currentCompany,
      expectedSalary: this.expectedSalary,
      skills: this.skills,
      experience: this.experience,
      curriculumFileKey: this.resumeFileKey || this.curriculumFileKey,
      curriculumFileName: this.resumeFileName || this.curriculumFileName,
      customFieldsAnswer: this.customFieldsAnswer,
      institutionType: this.institutionType,
      classification: this.classification,
      additionalInfo: this.additionalInfo,
      milestones: this.milestones,
      ...(this.referredByEmployeeId ? { referredByEmployeeId: this.referredByEmployeeId } : {}),
      ...(this.referralCode ? { referralCode: this.referralCode } : {}),
    };
  }
}

function mapAdmissionCustomFieldAnswers(raw: unknown): RecruitmentAdmissionCustomFieldAnswerModel[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .filter(item => !!item && typeof item === 'object')
    .map(item => RecruitmentAdmissionCustomFieldAnswerModel.create(item));
}
